import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { getToken } from "@/lib/clerk";

export interface DirectoryApp {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  iconPath: string | null;
  url: string;
  themeColor: string | null;
  category: string;
  hidden: boolean;
  /**
   * Every employee may use this app; no role has to be granted first.
   *
   * ⚠ Drives the access filter, and cannot be inferred from the directory answer —
   * an open app reports `registered: false` for anyone who has not signed in to it
   * yet, which is exactly what a real refusal looks like.
   */
  openToAll: boolean;
  /**
   * A superadmin has opted this app in to "Apps you can request". It is listed there
   * only if the app also says you have no access — see `requestableByAccess`.
   */
  requestable: boolean;
  /** When you last asked for access, while that request is inside its 7-day cooldown. */
  requestedAt: string | null;
  favourite: boolean;
  lastLaunchedAt: string | null;
}

const KEY = ["apps"] as const;

export function useApps() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<{ apps: DirectoryApp[] }>("/apps"),
    select: (d) => d.apps,
  });
}

export function useToggleFavourite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (app: DirectoryApp) =>
      api.put(`/apps/${app.id}/favourite`, { favourite: !app.favourite }),
    // Optimistic: starring should feel instant. The refetch below reconciles.
    onMutate: async (app) => {
      await qc.cancelQueries({ queryKey: KEY });
      const previous = qc.getQueryData<{ apps: DirectoryApp[] }>(KEY);
      qc.setQueryData<{ apps: DirectoryApp[] }>(KEY, (old) =>
        old
          ? {
              apps: old.apps.map((a) =>
                a.id === app.id ? { ...a, favourite: !a.favourite } : a,
              ),
            }
          : old,
      );
      return { previous };
    },
    onError: (_e, _app, ctx) => {
      if (ctx?.previous) qc.setQueryData(KEY, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Limits the API enforces on a request's reason; the form mirrors them. */
export const REASON_MIN = 10;
export const REASON_MAX = 1000;

/**
 * Ask the admin for access. The API emails them, copying you; Home itself grants
 * nothing. On success the app list is refetched so the card shows "Requested".
 */
export function useRequestAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ app, reason }: { app: DirectoryApp; reason: string }) =>
      api.post<{ ok: true; requestedAt: string }>(`/apps/${app.id}/access-request`, {
        reason,
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Records a launch without delaying navigation.
 *
 * `keepalive` is what makes this work: the browser is already tearing the page
 * down to follow the link, and a normal fetch would be cancelled mid-flight.
 * It bypasses the `api` helper for that flag, so it has to attach the bearer
 * token itself — without it every launch silently 401s in production.
 */
export function recordLaunch(appId: string): void {
  void (async () => {
    const token = await getToken();
    await fetch(`/api/apps/${appId}/launch`, {
      method: "POST",
      keepalive: true,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
  })().catch(() => {
    // A missed "recently used" entry is not worth surfacing to the user.
  });
}
